import { css, cx } from '@emotion/css';
import React from 'react';
import ReactDOM from 'react-dom';
import { Editor, Element as SlateElement, Transforms } from "slate";
import { useSlate } from "slate-react";
import LinkDialog from '../components/LinkDialog.jsx';
import { isAlignType, isBlockActive, isLinkActive, isListType, isMarkActive, unwrapLink, wrapLink } from "./richFormatterUtils.js";

export const Button = React.forwardRef(
    ({ className, active, reversed, ...props }, ref) => (
        <span
            {...props}
            ref={ref}
            className={cx(
                className,
                css`
          cursor: pointer;
          color: ${reversed
                        ? active
                            ? '#ccc'
                            : 'black'
                        : active
                            ? '#1976d2'
                            : 'black'};
        `
            )}
        />
    )
)

export const Icon = React.forwardRef(({ className, ...props }, ref) => (
    <span
        {...props}
        ref={ref}
        className={cx(
            'material-icons',
            className,
            css`
        font-size: 18px;
        vertical-align: text-bottom;
      `
        )}
    />
))

export const Instruction = React.forwardRef(({ className, ...props }, ref) => (
    <div
        {...props}
        ref={ref}
        className={cx(
            className,
            css`
        white-space: pre-wrap;
        margin: 0 -20px 10px;
        padding: 10px 20px;
        font-size: 14px;
        background: #f8f8e8;
      `
        )}
    />
))

export const Menu = React.forwardRef(({ className, ...props }, ref) => (
    <div
        {...props}
        data-test-id="menu"
        ref={ref}
        className={cx(
            className,
            css`
        & > * {
          display: inline-block;
        }

        & > * + * {
          margin-left: 15px;
        }
      `
        )}
    />
))

export const Portal = ({ children }) => {
    return typeof document === 'object'
        ? ReactDOM.createPortal(children, document.body)
        : null
}

export const Toolbar = React.forwardRef(({ className, ...props }, ref) => (
    <Menu
        {...props}
        ref={ref}
        className={cx(
            className,
            css`
        position: relative;
        padding: 10px;
        margin: 0px;
        background-color: #eee;
        // border-bottom: 2px solid #eee;
        margin-bottom: 10px;
      `
        )}
    />
))

export const BlockButton = ({ format, icon }) => {
    const editor = useSlate();
    return (
        <Button
            active={isBlockActive(
                editor,
                format,
                isAlignType(format) ? "align" : "type"
            )}
            onMouseDown={(event) => {
                event.preventDefault();
                toggleBlock(editor, format);
            }}
        >
            <Icon>{icon}</Icon>
        </Button>
    );
};

export const MarkButton = ({ format, icon }) => {
    const editor = useSlate();
    return (
        <Button
            active={isMarkActive(editor, format)}
            onMouseDown={(event) => {
                event.preventDefault();
                toggleMark(editor, format);
            }}
        >
            <Icon>{icon}</Icon>
        </Button>
    );
};

export const LinkButton = () => {
    const editor = useSlate();
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [savedSelection, setSavedSelection] = React.useState(null);

    const handleInsertLink = (url) => {
        setDialogOpen(false);

        const normalized = url.startsWith("http")
            ? url
            : `https://${url}`;

        if (savedSelection) {
            Transforms.select(editor, savedSelection);
        }

        wrapLink(editor, normalized);
        setSavedSelection(null);
    };

    const handleUnlink = () => {
        unwrapLink(editor);
    };

    return (
        <>
            <Button
                active={isLinkActive(editor)}
                onMouseDown={event => {
                    event.preventDefault();
                    if (isLinkActive(editor)) {
                        handleUnlink();
                    } else {
                        setSavedSelection(editor.selection);
                        setDialogOpen(true);
                    }
                }}
            >
                <Icon>link</Icon>
            </Button>
            <LinkDialog
                open={dialogOpen}
                onClose={() => {
                    setDialogOpen(false);
                    setSavedSelection(null);
                }}
                onSubmit={handleInsertLink}
            />
        </>
    );
};

export const toggleMark = (editor, format) => {
    const isActive = isMarkActive(editor, format);
    if (isActive) {
        Editor.removeMark(editor, format);
    } else {
        Editor.addMark(editor, format, true);
    }
};

const toggleBlock = (editor, format) => {
    const isActive = isBlockActive(
        editor,
        format,
        isAlignType(format) ? "align" : "type"
    );
    const isList = isListType(format);
    Transforms.unwrapNodes(editor, {
        match: (n) =>
            !Editor.isEditor(n) &&
            SlateElement.isElement(n) &&
            isListType(n.type) &&
            !isAlignType(format),
        split: true,
    });
    let newProperties;
    if (isAlignType(format)) {
        newProperties = {
            align: isActive ? undefined : format,
        };
    } else {
        newProperties = {
            type: isActive ? "paragraph" : isList ? "list-item" : format,
        };
    }
    Transforms.setNodes(editor, newProperties);
    if (!isActive && isList) {
        const block = { type: format, children: [] };
        Transforms.wrapNodes(editor, block);
    }
};